import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthFacadeService } from '../../domain/auth/service/auth-facade.service';
import { AuthActions } from '../../shared/store/auth/auth.actions';
import { AuthSelectors } from '../../shared/store/auth/auth.selectors';
import { isStoredTokenValid } from '../../shared/util/auth-token.util';

export const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
/** Refresh when token has less than this many seconds left (5 min buffer). */
const NEARLY_EXPIRED_BUFFER_SEC = 300;

@Injectable({ providedIn: 'root' })
export class TokenRefreshSchedulerService {
  private readonly store = inject(Store);
  private readonly authFacade = inject(AuthFacadeService);
  private intervalId: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.intervalId !== null) return;
    this.intervalId = setInterval(() => this.tick(), TOKEN_REFRESH_INTERVAL_MS);
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private tick(): void {
    const token = this.store.selectSnapshot(AuthSelectors.token);
    if (!token) return;

    const tokenSavedAt = this.store.selectSnapshot(AuthSelectors.tokenSavedAt);
    if (isStoredTokenValid(token, tokenSavedAt, NEARLY_EXPIRED_BUFFER_SEC)) {
      return;
    }

    this.store
      .dispatch(new AuthActions.RefreshToken())
      .pipe(
        catchError(() => {
          this.authFacade.sessionExpired();
          return EMPTY;
        })
      )
      .subscribe();
  }
}
